import { useEffect, useMemo, useState } from "react";
import { GeoJSON, useMapEvents } from "react-leaflet";
import L from "leaflet";
import type { FeatureCollection, Feature } from "geojson";
import { useUIStore, pushUrl } from "../../store/uiStore";

/** Listens for map clicks; exits drill-down if click lands outside `bounds`. */
function ExitOnOutsideClick({
  bounds,
  onExit,
}: {
  bounds: L.LatLngBounds | null;
  onExit: () => void;
}) {
  useMapEvents({
    click(e) {
      if (bounds && !bounds.contains(e.latlng)) onExit();
    },
  });
  return null;
}

interface TractProps {
  GEOID:                string;
  COUNTYFP:             string;
  county:               string | null;
  max_p_hat:            number | null;
  max_p_tilde:          number | null;          // raw routing (can exceed 1)
  max_p_tilde_display:  number | null;          // sigmoid display, [0,1]
  cell_count:           number | null;
  quintile:             string | null;
}

function colorFor(value: number | null): string {
  if (value == null || isNaN(value)) return "#e5e7eb";  // muted gray (R8)
  if (value >= 0.8) return "#7f1d1d";
  if (value >= 0.6) return "#b91c1c";
  if (value >= 0.4) return "#ef4444";
  if (value >= 0.2) return "#fb923c";
  return "#fde68a";
}

export function TractDrillDown() {
  const { choroplethMode, drillCounty, selectCell, setDrillCounty } = useUIStore();
  const [data, setData] = useState<FeatureCollection | null>(null);
  // Tract GEOID → representative cell_id (lat-lon string format that the
  // backend keys cell_predictions on). Without this, clicking a tract
  // tries to fetch /cells/{tract_geoid} which 404s.
  const [tractToCell, setTractToCell] = useState<Record<string, string> | null>(null);

  useEffect(() => {
    if (!drillCounty) return;
    fetch("/tracts.geojson")
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData(null));
  }, [drillCounty]);

  useEffect(() => {
    fetch("/tract_to_cell.json")
      .then((r) => (r.ok ? r.json() : null))
      .then(setTractToCell)
      .catch(() => setTractToCell(null));
  }, []);

  // Filter tracts by county prefix (GEOID begins with county_geoid)
  const filtered = useMemo<FeatureCollection | null>(() => {
    if (!data || !drillCounty) return null;
    const features = data.features.filter((f) => {
      const props = (f.properties ?? {}) as TractProps;
      const geoid = (props.GEOID ?? "").toString();
      return geoid.startsWith(drillCounty);
    });
    return { type: "FeatureCollection", features };
  }, [data, drillCounty]);

  // Bounding box of all tracts in the drilled county. A click outside this
  // bbox exits drill-down (back to county view).
  const bounds = useMemo<L.LatLngBounds | null>(() => {
    if (!filtered || filtered.features.length === 0) return null;
    return L.geoJSON(filtered).getBounds();
  }, [filtered]);

  const exitDrill = () => {
    setDrillCounty(null);
    pushUrl({ drillCounty: null });
  };

  if (!filtered) return null;

  const styleFn = (feature?: Feature) => {
    const props = (feature?.properties ?? {}) as TractProps;
    const value = choroplethMode === "raw" ? props.max_p_hat : props.max_p_tilde_display;
    return {
      fillColor: colorFor(value),
      weight: 0.5,
      color: "#1f2937",
      fillOpacity: value == null ? 0.15 : 0.7,
    };
  };

  return (
    <>
      <ExitOnOutsideClick bounds={bounds} onExit={exitDrill} />
      <GeoJSON
        key={`tracts-${drillCounty}-${choroplethMode}`}
        data={filtered}
        style={styleFn}
        onEachFeature={(feature, layer) => {
          const props = feature.properties as TractProps;
          layer.bindTooltip(
            `<b>Tract ${props.GEOID}</b><br/>` +
              `${props.county ?? ""}<br/>` +
              `SVI ${props.quintile ?? "n/a"}<br/>` +
              `max p̂: ${props.max_p_hat?.toFixed(3) ?? "n/a"}<br/>` +
              `max p̃: ${props.max_p_tilde_display?.toFixed(3) ?? "n/a"}`,
            { sticky: true },
          );
          layer.on({
            click: (e) => {
              // Stop propagation so the map's click handler (ExitOnOutsideClick)
              // does not fire and exit drill-down when clicking on a tract.
              L.DomEvent.stopPropagation(e);
              // Resolve the tract GEOID to a real cell_id (lat-lon string)
              // that exists in cell_predictions. The mapping picks the
              // max-p̃ cell within the tract.
              const cellId = tractToCell?.[props.GEOID];
              if (!cellId) {
                // No cell for this tract — likely tract has no predictions.
                // Don't open the drawer; let the muted-gray styling speak.
                return;
              }
              selectCell(cellId);
              pushUrl({ selectedCellId: cellId });
            },
          });
        }}
      />
    </>
  );
}
